import { EmbedClient } from '../../login/v1/typings';
import { DT_EMBED_CLIENT_STORAGE_KEY } from '../constants/embed-client-storage';

const SF_TARGET_URL_KEY = 'DT_SF_TARGET_URL';

export function parseEmbedClient(value: string | null): EmbedClient | null {
  if (!value) {
    return null;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === 'salesforce') {
    return EmbedClient.SALESFORCE;
  }
  if (normalized === 'salesforcev2') {
    return EmbedClient.SALESFORCEV2;
  }
  if (normalized === 'dynamics365') {
    return EmbedClient.DYNAMICS365;
  }
  return null;
}

/**
 * Synchronous read of Salesforce embed client from URL or session storage.
 * Used at navigation time so values persisted after login are visible even when
 * useEmbedClient's memo has not re-run.
 */
export function getEmbedClientFromBrowser(): EmbedClient | null {
  const fromUrl = parseEmbedClient(new URLSearchParams(window.location.search).get('embedClient'));
  if (fromUrl) {
    return fromUrl;
  }
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    return parseEmbedClient(sessionStorage.getItem(DT_EMBED_CLIENT_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * True when embedClient in URL or session is a CRM iframe embed (Salesforce or Dynamics 365).
 * Kept under the legacy name `isSalesforceEmbedClient` so existing call sites stay stable —
 * the iframe history hacks and replace-navigation behavior apply equally to all CRM embeds.
 */
export function isSalesforceEmbedClient(): boolean {
  const client = getEmbedClientFromBrowser();
  return client === EmbedClient.SALESFORCE || client === EmbedClient.SALESFORCEV2 || client === EmbedClient.DYNAMICS365;
}

// ---------------------------------------------------------------------------
// Salesforce iframe history management
// ---------------------------------------------------------------------------

type HistoryWithDtPatch = History & { __dtSalesforcePushStatePatched?: boolean };

let _lastSavedTargetUrl: string | null = null;

/** Detect whether this page load was triggered by the browser back/forward button. */
function isBackForwardNavigation(): boolean {
  try {
    const entries = performance.getEntriesByType('navigation');
    if (entries.length > 0) {
      return (entries[0] as PerformanceNavigationTiming).type === 'back_forward';
    }
  } catch {
    // API unavailable
  }
  return false;
}

function saveSalesforceTargetUrlIfNeeded(): void {
  const url = window.location.pathname + window.location.search;
  const path = window.location.pathname;
  if (path.startsWith('/embed/login') || path.startsWith('/contact-support') || path.includes('/error')) {
    return;
  }
  if (url === _lastSavedTargetUrl) {
    return;
  }
  _lastSavedTargetUrl = url;
  try {
    sessionStorage.setItem(SF_TARGET_URL_KEY, url);
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * On page load, if the iframe was reloaded by a back-button press, call
 * history.back() to chain through all iframe history entries until we exit
 * to the parent Salesforce window.
 *
 * For fresh loads (Salesforce setting iframe src), let the page load normally.
 *
 * Must run BEFORE React Router mounts.
 */
export function salesforceRedirectIfNeeded(): void {
  if (typeof window === 'undefined') {
    return;
  }
  if (!isSalesforceEmbedClient()) {
    return;
  }
  try {
    const targetUrl = sessionStorage.getItem(SF_TARGET_URL_KEY);
    if (!targetUrl) {
      return;
    }

    const currentUrl = window.location.pathname + window.location.search;
    if (currentUrl === targetUrl) {
      return;
    }

    if (isBackForwardNavigation()) {
      window.history.back();
      return;
    }

    // Fresh load (not back/forward). If it's a login page with auth params,
    // clear the stale target so the new login flow sets a fresh one.
    if (window.location.pathname.startsWith('/embed/login')) {
      const params = new URLSearchParams(window.location.search);
      if (params.has('userId') || params.has('singleUseToken')) {
        sessionStorage.removeItem(SF_TARGET_URL_KEY);
        _lastSavedTargetUrl = null;
      }
    }
  } catch {
    // sessionStorage unavailable
  }
}

/**
 * Patches history.pushState → replaceState for Salesforce embeds so the iframe
 * does not accumulate session-history entries. Also wraps replaceState to keep
 * the target URL in sync, and installs a capture-phase popstate handler to chain
 * history.back() on same-document back/forward events.
 *
 * Must run before react-router-dom loads so the router does not capture the
 * native pushState reference first.
 */
export function installSalesforceEmbedHistoryPushStatePatch(): void {
  if (typeof window === 'undefined' || !window.history) {
    return;
  }
  const history = window.history as HistoryWithDtPatch;
  if (history.__dtSalesforcePushStatePatched) {
    return;
  }
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = function pushState(data: unknown, unused: string, url?: string | URL | null): void {
    if (isSalesforceEmbedClient()) {
      originalReplaceState(data, unused, url);
      saveSalesforceTargetUrlIfNeeded();
      return;
    }
    originalPushState(data, unused, url);
  };

  history.replaceState = function replaceState(data: unknown, unused: string, url?: string | URL | null): void {
    originalReplaceState(data, unused, url);
    if (isSalesforceEmbedClient()) {
      saveSalesforceTargetUrlIfNeeded();
    }
  };

  history.__dtSalesforcePushStatePatched = true;

  // Capture-phase popstate handler — fires before React Router.
  // On same-document back/forward, chain history.back() to exit the iframe.
  window.addEventListener(
    'popstate',
    (e: PopStateEvent) => {
      if (!isSalesforceEmbedClient()) {
        return;
      }
      try {
        const targetUrl = sessionStorage.getItem(SF_TARGET_URL_KEY);
        if (!targetUrl) {
          return;
        }
        const currentUrl = window.location.pathname + window.location.search;
        if (currentUrl === targetUrl) {
          return;
        }
        e.stopImmediatePropagation();
        window.history.back();
      } catch {
        // silent
      }
    },
    { capture: true }
  );
}
